import React, { FunctionComponent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { get, sortBy } from 'lodash';
import { ISelectBoardListInputFormProps, ISelectInputFormProps } from '../types';
import Select, { OptionsType, OptionTypeBase, StylesConfig } from 'react-select';
import { useSelectWithParams } from '../../../../../utils/selector.utils';
import { boardListSelectors } from '../../../../../app/resource/boards/boardList/boardList.selectors';
import { IBoardList } from '../../../../../app/resource/boards/boardList/boardList.types';

const noBoardListOption = {
  value: '',
  label: 'No List',
};

export const SelectBoardListInput: FunctionComponent<ISelectBoardListInputFormProps> = ({
  id = 'boardList',
  name = 'boardList',
  defaultBoardListId,
  dependencyFieldName = 'board',
  validation,
  disabled,
  placeholder = 'Select List',
  rightIcon,
  leftIcon,
  label = 'Board List',
  isSingle,
  className,
  clearAllActionAvailable = true,
  onInputChange,
  onGetOptionLabel,
}) => {
  const { control, errors, setError, reset, clearErrors } = useFormContext();

  useEffect(() => {
    if (validation) {
      if (!errors[name] && control.getValues()[name] && control.getValues()[name].length < 1) {
        if (name === 'status') {
          setError(name, { type: 'minLength', message: 'You need to select a status to save' });
        } else {
          setError(name, { type: 'minLength', message: 'You need to add at least one entity' });
        }
      }
    } else if (errors[name]) {
      clearErrors(name);
    }
  }, [control, errors, name, setError, clearErrors, validation]);

  const handleChange = (e: any, controllerProps: any) => {
    if (onInputChange) {
      onInputChange(controllerProps.name);
    }
    controllerProps.onChange(e);
    setChanged(true);
  };

  const CustomClearIndicator = () => {
    return null;
  };

  const buildStyle = (items: number) => {
    const MIN_ITEMS_FOR_UPDATE_UI = 2;
    const customStyles: StylesConfig<OptionsType<OptionTypeBase>, true> = {
      control: (customStyles) => ({
        ...customStyles,
        boxShadow: '0 !important',
        borderColor: '#edf2f5',
        paddingTop: items >= MIN_ITEMS_FOR_UPDATE_UI ? 10 : undefined, // Fix UI padding error when too many items are selected
        '&:hover': {
          borderColor: '#edf2f5',
          backgroundColor: '#edf2f5',
        },
      }),
      multiValue: (customStyles) => ({
        ...customStyles,
        height: 'auto',
      }),
      valueContainer: (customStyles) => ({
        ...customStyles,
        flexWrap: 'wrap',
        maxHeight: '150px',
        overflowY: items >= MIN_ITEMS_FOR_UPDATE_UI ? 'auto' : undefined, // Fix UI scroll error when too many items are selected
      }),
      menuList: (customStyles) => ({
        ...customStyles,
        maxHeight: '150px',
        overflowY: 'scroll',
      }),
      placeholder: (customStyles) => ({
        ...customStyles,
        color: '#D8D8D8',
        fontWeight: 'bold',
        letterSpacing: '0.4px',
      }),
      indicatorSeparator: (customStyles) => ({
        ...customStyles,
        borderColor: '#edf2f5',
      }),
    };
    return customStyles;
  };

  const [changed, setChanged] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [boardListOptions, setBoardListOptions] = useState<any | any[]>([noBoardListOption]);
  const [defaultValue, setDefaultValue] = useState<{ value: string; label: string } | undefined>(noBoardListOption);

  const formProps = useFormContext();
  const focusBoardTuple = formProps.watch(dependencyFieldName);

  let boardLists = [] as IBoardList[];
  let boardId = '';
  if (focusBoardTuple) {
    boardId = focusBoardTuple.value;
  }

  boardLists = useSelectWithParams(boardListSelectors.selectResourcesByBoardId, boardId) as IBoardList[];

  useEffect(() => {
    if (focusBoardTuple && focusBoardTuple.value !== selectedBoardId) {
      setSelectedBoardId(focusBoardTuple.value);
      let boardListOptions:
        | {
            value: string;
            label: string;
          }
        | {
            value: string;
            label: string;
          }[] = [];

      if (boardLists.length == 0) {
        boardListOptions.unshift(noBoardListOption);
      } else {
        boardListOptions = boardListOptions.concat(
          sortBy(
            boardLists.map((board) => board && { value: board.id, label: board.name }),
            ['label'],
          ),
        );
      }

      setBoardListOptions(boardListOptions);

      let foundOption: any;
      if (boardListOptions.length == 1) {
        setDefaultValue(boardListOptions[0]);
      } else if (!changed) {
        if (defaultBoardListId) {
          foundOption = boardListOptions.find((boardList) => boardList && boardList.value === defaultBoardListId);
          if (foundOption) {
            setDefaultValue(foundOption);
          } else {
            setDefaultValue({ value: '', label: '' });
          }
        } else {
          setDefaultValue({ value: '', label: '' });
        }
      }
      setChanged(true);

      if (foundOption) {
        reset({
          [name]: foundOption,
        });
      }
    }
  }, [
    name,
    reset,
    setSelectedBoardId,
    selectedBoardId,
    focusBoardTuple,
    setBoardListOptions,
    boardListOptions,
    boardLists,
    changed,
    defaultBoardListId,
  ]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ''}
      rules={validation}
      disabled={disabled}
      render={(controllerProps) => (
        <>
          {!!get(errors, name, false) && (
            <label htmlFor={name} style={{ color: 'red' }}>
              {`${label ? label + ' - ' : ''} ${get(errors, `${name}.message`)}`}
            </label>
          )}
          {!get(errors, name, false) && <label htmlFor={name}>{label}</label>}
          {leftIcon}
          {rightIcon}
          <Select
            defaultValue={defaultValue || ''}
            id={id}
            className={`custom-select-new ${className}`}
            name={controllerProps.name}
            disabled={disabled}
            onChange={(e) => handleChange(e, controllerProps)}
            options={boardListOptions}
            value={controllerProps.value}
            placeholder={placeholder}
            isMulti={isSingle ? undefined : true}
            closeMenuOnSelect={isSingle}
            styles={buildStyle(controllerProps.value.length)}
            getOptionLabel={onGetOptionLabel}
            components={clearAllActionAvailable ? undefined : { ClearIndicator: CustomClearIndicator }}
          />
        </>
      )}
    />
  );
};
